import { BaseDiv, Button, ErrorText, Heading1, Heading3, LoadingAnimation, ModuleContainer, P } from '@cmctechnology/webinvest-design-system';
import { frontendActions } from '@cmctechnology/webinvest-store-frontend';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

const PRO_INFO_GET_API_NAME = 'ClientProSubscriptionGet1';
const SUBCRIBE_PRO_API_NAME = 'ClientProSubscriptionUpdate1';

const PageContainer = styled(BaseDiv)`
  margin: 1.4rem 2rem;
`;

const ModuleContainerStyled = styled(ModuleContainer)`
  flex: 1 1 0;
  width: 100%;
  padding: 2rem;
`;

const ButtonContainer = styled(BaseDiv)`
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
  width: 14rem;
`;

type ClientProSubscriptionGet1Response = {
  EndDate: string;
  Success: Boolean;
  CancelToken: string;
  UiMessage?: string;
};

function getFirstDayOfNextMonth() {
  const today = new Date();
  // Create a date for the "1st" day of the next month
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  return lastDay;
}

type SubscriptionInfoStatus = 'loading' | 'success' | 'error';
type SubscriptionUpdateStatus = 'in-progress' | 'success' | 'error' | undefined;

const CardBody = ({
  subscriptionInfoStatus,
  subcriptionInfo
}: {
  subscriptionInfoStatus: SubscriptionInfoStatus;
  subcriptionInfo: ClientProSubscriptionGet1Response | undefined;
}) => {
  const { t } = useTranslation();

  if (subscriptionInfoStatus === 'loading') {
    return <LoadingAnimation size='large' />;
  }
  if (subscriptionInfoStatus === 'error') {
    return <ErrorText>{'Error fetching subscription info'}</ErrorText>;
  }

  const expiryDateString = subcriptionInfo?.EndDate && new Date(subcriptionInfo?.EndDate).toLocaleString('en-AU');

  // find if expiryDate is last day of current month or earlier
  let isExpiryWithinCurrentMonth = false;

  /* istanbul ignore next */
  if (expiryDateString) {
    const expiryDate = new Date(subcriptionInfo?.EndDate);
    const firstDayOfNextMonth = getFirstDayOfNextMonth();

    isExpiryWithinCurrentMonth = expiryDate.getTime() <= firstDayOfNextMonth.getTime();
  }

  return (
    <>
      {isExpiryWithinCurrentMonth && <P gutterBottom={true}>{t('You have previously submitted your request to unsubscribe from CMC Invest Pro.')}</P>}
      <P gutterBottom={true}>
        {t('Your current CMC Invest Pro subscription expires on ')} {expiryDateString}{' '}
      </P>
    </>
  );
};

export const ProUnsubscribe = () => {
  const { t } = useTranslation();
  const [subscriptionInfoStatus, setSubscriptionInfoStatus] = useState<SubscriptionInfoStatus>('loading');
  const [subscriptionUpdateStatus, setSubscriptionUpdateStatus] = useState<SubscriptionUpdateStatus>(undefined);
  const [subcriptionInfo, setSubcriptionInfo] = useState<ClientProSubscriptionGet1Response>();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSubscriptionInfo = async () => {
      try {
        const response = await frontendActions.webTradingApiCallDirect(PRO_INFO_GET_API_NAME, {}, true, true, true);
        const responseData = response as ClientProSubscriptionGet1Response;

        /* istanbul ignore next */
        if (!responseData.Success) {
          /* istanbul ignore next */ setSubscriptionInfoStatus('error');
          /* istanbul ignore next */ return;
        }

        setSubcriptionInfo(responseData);
        setSubscriptionInfoStatus('success');
      } catch (error) {
        console.error('Error fetching subscription info:', error);
        setSubscriptionInfoStatus('error');
      }
    };

    fetchSubscriptionInfo();
  }, []);

  const handleUnsubscribe = async () => {
    if (!subcriptionInfo?.CancelToken) {
      return;
    }

    const requestBody = {
      CancelToken: subcriptionInfo.CancelToken
    };

    setSubscriptionUpdateStatus('in-progress');

    try {
      const confirmationResponse = (await frontendActions.webTradingApiCallDirect(SUBCRIBE_PRO_API_NAME, requestBody, true, true, true)) as {
        Success: boolean;
        UiMessage: string | null;
      };

      if (confirmationResponse.Success) {
        setSubscriptionUpdateStatus('success');
        navigate('/platforms');
        window.location.reload();
      } else {
        setSubscriptionUpdateStatus('error');
      }
    } catch {
      setSubscriptionUpdateStatus('error');
    }
  };

  const handleCancel = () => {
    navigate('/platforms');
    window.location.reload();
  };

  return (
    <PageContainer>
      <Heading1 headingType='SEMI_BOLD'>{t('Unsubscribe')}</Heading1>
      <ModuleContainerStyled>
        <Heading3 headingType='SEMI_BOLD'>{t('Unsubscribe from the Pro platform')}</Heading3>
        <>
          <CardBody subscriptionInfoStatus={subscriptionInfoStatus} subcriptionInfo={subcriptionInfo} />
          <P>
            {t(
              'Your CMC Invest Pro subscription includes benefits such as multiple layouts, sophisticated market depth display, module linking and complimentary access to Dynamic Data to enhance your trading efficiency. If you have any further questions please contact us.'
            )}
          </P>
        </>
        <ButtonContainer>
          <Button
            variant='primary'
            disabled={subscriptionInfoStatus === 'loading' || subscriptionUpdateStatus === 'in-progress'}
            processing={subscriptionUpdateStatus === 'in-progress'}
            onClick={handleUnsubscribe}
          >
            {t('Unsubscribe')}
          </Button>
          <Button onClick={handleCancel} variant='secondary'>
            {t('Cancel')}
          </Button>
        </ButtonContainer>
        {subscriptionUpdateStatus === 'error' && <ErrorText>{t('Error unsubscribing from Pro platform')}</ErrorText>}
      </ModuleContainerStyled>
    </PageContainer>
  );
};
